import {
  Children,
  type FC,
  type ForwardedRef,
  forwardRef,
  Fragment,
  isValidElement,
  type MutableRefObject,
  type ReactElement,
  type ReactNode,
  type RefAttributes,
  useCallback,
  useEffect,
  useState,
} from 'react'
import { FormattedMessage } from 'react-intl'
import { Paper, type PaperProps, Typography } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { rem } from 'polished'

import { useIsMediumUp } from '@Hooks/useResponsive'
import { TABBAR_HEIGHT } from '@Styles/constants/layout'
import { type EmptyProps } from '@Types/component'
import { remToNumberOfPixels } from '@Utils/units'

import {
  type AutocompleteBaseProps,
  type AutocompleteItemModel,
  type AutocompleteItemProps,
} from './Autocomplete.types'
import * as styles from './styles'

export type GenericAutocompleteProps<AutocompleteItemModelType extends AutocompleteItemModel> = Omit<
  AutocompleteBaseProps<AutocompleteItemModelType>,
  'autocompleteRef'
> & {
  AutocompleteItemComponent: (
    props: AutocompleteItemProps<AutocompleteItemModelType> & RefAttributes<HTMLDivElement>,
  ) => ReactNode
  recalculateMaxHeightAfterDelayInMs?: number
}

const HiddenAutocompleteDescription: FC<EmptyProps> = () => (
  <div hidden id="autocomplete_input_visuallyHiddenLabel">
    <FormattedMessage id="autocomplete_input_visuallyHiddenLabel" />
  </div>
)

const BOTTOM_SCREEN_GAP = rem(40)

const GenericAutocomplete = <AutocompleteItemModelType extends AutocompleteItemModel>(
  {
    AutocompleteItemComponent,
    children,
    shouldOpen = false,
    onOpen,
    onClose,
    onSelectItem,
    onMouseMove,
    onItemFocus,
    onItemHover,
    highlighted,
    sections,
    itemRef,
    recalculateMaxHeightAfterDelayInMs = 0,
    ...rest
  }: GenericAutocompleteProps<AutocompleteItemModelType> & Omit<PaperProps, 'itemRef'>,
  ref: ForwardedRef<HTMLDivElement>,
): ReactElement | null => {
  const [isOpen, setIsOpen] = useState(false)
  const [maxHeight, setMaxHeight] = useState<number | undefined>()
  const theme = useTheme()
  const isMediumUp = useIsMediumUp(theme)

  const computeAutocompleteAvailableHeight = useCallback(() => {
    if (!ref) {
      return
    }
    const { current } = ref as MutableRefObject<HTMLDivElement | null>

    const tabBarHeight = isMediumUp ? 0 : remToNumberOfPixels(TABBAR_HEIGHT)

    if (current?.parentElement) {
      setMaxHeight(
        window.innerHeight -
          current.parentElement.getBoundingClientRect().top -
          remToNumberOfPixels(BOTTOM_SCREEN_GAP) -
          tabBarHeight,
      )
    }
  }, [isMediumUp, ref])

  useEffect(() => {
    if (isOpen) {
      computeAutocompleteAvailableHeight()

      if (recalculateMaxHeightAfterDelayInMs > 0) {
        const timeout = setTimeout(computeAutocompleteAvailableHeight, recalculateMaxHeightAfterDelayInMs)

        return () => clearTimeout(timeout)
      }
    }

    return () => {}
  }, [computeAutocompleteAvailableHeight, isOpen, recalculateMaxHeightAfterDelayInMs])

  useEffect(() => {
    const validChildren = Children.toArray(children).filter(isValidElement)

    const isACOpen = shouldOpen && (!!sections.length || validChildren.length > 0)
    setIsOpen(isACOpen)

    if (isACOpen) {
      onOpen?.()
    } else {
      onClose?.()
    }
  }, [shouldOpen, sections.length, children, onOpen, onClose])

  if (!isOpen) {
    return <HiddenAutocompleteDescription />
  }

  return (
    <>
      <HiddenAutocompleteDescription />
      <Paper
        {...rest}
        css={styles.autocomplete(theme, maxHeight)}
        elevation={0}
        ref={ref}
        tabIndex={-1}
        role="listbox"
        onMouseMove={() => onMouseMove?.()}
      >
        {children}
        {sections.map(({ title, items }, index) => (
          <Fragment key={title}>
            {title && (
              <Typography
                variant="subtitle1"
                color="textSecondary"
                css={[styles.sectionTitle(theme), index === 0 && !children && styles.sectionTitleFirstChild]}
              >
                {title}
              </Typography>
            )}
            {items.map((item) => {
              const { id } = item
              const isHighlighted = item === highlighted || id === highlighted?.id

              return (
                // eslint-disable-next-line jsx-a11y/click-events-have-key-events
                <div
                  key={id}
                  id={id}
                  data-test={id}
                  role="option"
                  aria-selected={isHighlighted}
                  onClick={() => onSelectItem?.(item)}
                  onMouseOver={() => onItemHover?.(item)}
                  onFocus={() => onItemFocus?.(item)}
                  tabIndex={-1}
                >
                  <AutocompleteItemComponent
                    isHighlighted={isHighlighted}
                    ref={isHighlighted ? itemRef : undefined}
                    model={item}
                  />
                </div>
              )
            })}
          </Fragment>
        ))}
      </Paper>
    </>
  )
}

export default forwardRef(GenericAutocomplete)
